import { BehaviorSubject } from 'rxjs';
import { OutputMode, YearOrdering } from './date-picker.enum';
import { Observable } from 'rxjs';
import { isNumber, isString } from 'util';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
var DatePickerService = /** @class */ (function () {
    function DatePickerService() {
        var _this = this;
        // Constants
        this.now = Observable.of(moment());
        this.monthNames = moment.monthsShort();
        this._100yearsAgo = this.now.map(function (d) { return d.clone().subtract(100, 'years'); });
        this._100yearsFromNow = this.now.map(function (d) { return d.clone().add(100, 'years'); });
        this._0_to_11_months = _.range(0, 12).map(function (i) { return [i, _this.monthNames[i]]; });
        this._1_to_31 = _.range(1, 32);
        // Value
        this.input = new Subject(); // 0 index
        this.month = new BehaviorSubject(null); // 0 index
        this.day = new BehaviorSubject(null);
        this.year = new BehaviorSubject(null);
        // Input
        this.min = new BehaviorSubject(null);
        this.max = new BehaviorSubject(null);
        this.yearOrdering = new BehaviorSubject(YearOrdering.descending);
        this.outputMode = new BehaviorSubject(OutputMode.full);
        // Output
        this.outputMoment = Observable
            .combineLatest(this.year, this.month, this.day)
            .map(function (_a) {
            var y = _a[0], m = _a[1], d = _a[2];
            if (isNumber(y) && isNumber(m)) {
                if (isNumber(d)) {
                    return moment([y, m, d]);
                }
                else {
                    return moment([y, m]);
                }
            }
            return null;
        });
        this.yearMonth = this.outputMoment
            .map(function (m) { return m && m.format('YYYY-MM'); });
        this.iso8601 = this.outputMoment.map(function (m) { return m && m.format('YYYY-MM-DD'); });
        this.outputDate = this.outputMoment.map(function (m) { return m && m.toDate(); });
        this.output = Observable
            .merge(this.year, this.month, this.day, this.outputMode)
            .flatMap(function () { return _this.outputMode; })
            .flatMap(function (outputMode) {
            switch (outputMode) {
                case OutputMode.full:
                    return _this.iso8601;
                case OutputMode.yearMonth:
                    return _this.yearMonth;
                case OutputMode.date:
                    return _this.outputDate;
                case OutputMode.moment:
                    return _this.outputMoment;
                default:
                    return Observable.of(null);
            }
        }).map(function (o) { return o || null; });
        // Derivatives
        this.minMoment = this.min.map(function (min) { return min && moment(min); });
        this.maxMoment = this.max.map(function (max) { return max && moment(max); });
        this.minMaxHasSense = Observable.combineLatest(this.minMoment, this.maxMoment)
            .map(function (_a) {
            var min = _a[0], max = _a[1];
            if (min && max) {
                return !min.isAfter(max);
            }
            return true;
        });
        this.minYearDropDown = Observable
            .combineLatest(this.minMoment, this.maxMoment)
            .flatMap(function (_a) {
            var minMoment = _a[0], maxMoment = _a[1];
            if (minMoment) {
                return Observable.of(minMoment);
            }
            if (maxMoment) {
                return Observable.of(maxMoment.clone().subtract(100, 'years'));
            }
            return _this._100yearsAgo;
        })
            .map(function (d) { return d && d.get('year'); });
        this.maxYearDropDown = Observable
            .combineLatest(this.minMoment, this.maxMoment)
            .flatMap(function (_a) {
            var minMoment = _a[0], maxMoment = _a[1];
            if (maxMoment) {
                return Observable.of(maxMoment);
            }
            if (minMoment) {
                return Observable.of(minMoment.clone().add(100, 'years'));
            }
            return _this._100yearsFromNow;
        })
            .map(function (d) { return d && d.get('year'); });
        this.minYearMonth = this.minMoment.map(function (d) { return d && d.format('YYYY-MM'); });
        this.maxYearMonth = this.maxMoment.map(function (d) { return d && d.format('YYYY-MM'); });
        this.minDay = this.minMoment.map(function (d) { return d && d.get('date'); });
        this.maxDay = this.maxMoment.map(function (d) { return d && d.get('date'); });
        this.hasAnyDatePart = Observable
            .combineLatest(this.year, this.month, this.day)
            .map(function (_a) {
            var y = _a[0], m = _a[1], d = _a[2];
            return isNumber(y) && isNumber(m) && isNumber(d);
        });
        // Choices
        this.yearList = Observable.combineLatest(this.minMaxHasSense, this.minYearDropDown, this.maxYearDropDown, this.yearOrdering).map(function (_a) {
            var minMaxHasSense = _a[0], minYear = _a[1], maxYear = _a[2], yearOrdering = _a[3];
            // If min and max don't make sense, don't bother
            if (!minMaxHasSense) {
                return [];
            }
            var yearList = _.range(minYear, maxYear + 1);
            if (yearOrdering === YearOrdering.descending) {
                yearList = yearList.reverse();
            }
            return yearList;
        });
        this.monthList = Observable.combineLatest(this.minMaxHasSense, this.minMoment, this.maxMoment, this.year).map(function (_a) {
            var hasSense = _a[0], minMoment = _a[1], maxMoment = _a[2], choosenYear = _a[3];
            // If min and max don't make sense, don't bother
            if (!hasSense) {
                return [];
            }
            // If no min or max, don't bother
            if (!minMoment && !maxMoment) {
                return _this._0_to_11_months;
            }
            var hasChoosenYear = isNumber(choosenYear);
            var minYear = minMoment && minMoment.get('year');
            var maxYear = maxMoment && maxMoment.get('year');
            var minMonth = minMoment && minMoment.get('month');
            var maxMonth = maxMoment && maxMoment.get('month');
            var minMaxSameYear = minYear === maxYear;
            var finalMinMonth = 0;
            var finalMaxMonth = 11;
            // If min and max doesn't make sense, return empty
            if (minMoment && maxMoment && minMoment.isAfter(maxMoment)) {
                return [];
            }
            // Bound months
            if (hasChoosenYear && minYear === choosenYear || minMaxSameYear) {
                finalMinMonth = minMonth;
            }
            if (hasChoosenYear && maxYear === choosenYear || minMaxSameYear) {
                finalMaxMonth = maxMonth;
            }
            return _.range(finalMinMonth, finalMaxMonth + 1).map(function (i) { return [i, _this.monthNames[i]]; });
        });
        this.dayList = Observable.combineLatest(this.minMaxHasSense, this.minYearMonth, this.maxYearMonth, this.minDay, this.maxDay, this.yearMonth).map(function (_a) {
            var hasSense = _a[0], minYearMonth = _a[1], maxYearMonth = _a[2], minDay = _a[3], maxDay = _a[4], choosenYearMonth = _a[5];
            // If min and max don't make sense, don't bother
            if (!hasSense) {
                return [];
            }
            // If min and max are the same month year
            if (minYearMonth && maxYearMonth && minYearMonth === maxYearMonth) {
                return _.range(minDay, maxDay + 1);
            }
            // If month and year hasn't been chosen return 1 to 31
            if (!choosenYearMonth) {
                return _this._1_to_31;
            }
            var finalMinDay = 1;
            var finalMaxDay = 31;
            if (choosenYearMonth) {
                if (choosenYearMonth === minYearMonth) {
                    finalMinDay = minDay;
                }
                if (choosenYearMonth === maxYearMonth) {
                    finalMaxDay = maxDay;
                }
                else if (choosenYearMonth) {
                    finalMaxDay = moment(choosenYearMonth, 'YYYY-MM').endOf('month').get('date');
                }
            }
            return _.range(finalMinDay, finalMaxDay + 1);
        });
        this.monthListValues = this.monthList.map(function (mL) { return mL.map(function (_a) {
            var i = _a[0];
            return i;
        }); });
        this.input.subscribe(function (input) { return _this.setInboundValue(input); });
        Observable.combineLatest(this.yearList, this.year, this.monthListValues, this.month, this.dayList, this.day, this.outputMoment).debounceTime(5).subscribe(function (values) {
            var _a = values, yearList = _a[0], year = _a[1], monthListValues = _a[2], month = _a[3], dayList = _a[4], day = _a[5], momentValue = _a[6];
            if (isNumber(year) && !yearList.includes(year)) {
                _this.year.next(null);
            }
            if (isNumber(month) && !monthListValues.includes(month)) {
                _this.month.next(null);
            }
            if (isNumber(day)) {
                if (!dayList.includes(day)) {
                    _this.day.next(null);
                }
                else if (momentValue && day > momentValue.get('date')) {
                    _this.day.next(null);
                }
            }
        });
    }
    DatePickerService.prototype.setInboundValue = function (input) {
        if (!input) {
            this.year.next(null);
            this.month.next(null);
            this.day.next(null);
        }
        else {
            if (isString(input)) {
                input = moment(input, 'YYYY-MM-DD');
            }
            if (moment.isMoment(input)) {
                var date = input;
                this.year.next(date.get('year'));
                this.month.next(date.get('month'));
                this.day.next(date.get('date'));
            }
            else if (moment.isDate(input)) {
                var date = input;
                this.year.next(date.getFullYear());
                this.month.next(date.getMonth());
                this.day.next(date.getDate());
            }
        }
    };
    return DatePickerService;
}());
export { DatePickerService };
